import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../OnboardingWizard.css';
import EmbeddedPayment from '../components/EmbeddedPayment';

const AccountSetupFlow = ({ 
  leadData, 
  planData, 
  leadId, 
  currentFlow, 
  onPrevious, 
  onCreateCheckout,
  onActivateFreeMode,
  isFreeMode = false,
  billingConfig = {},
  isLoading, 
  error,
  paymentPreload 
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if we're in dev mode - this takes absolute precedence
  const isDevMode = searchParams.get('dev_mode') === 'true';
  const devFlowParam = searchParams.get('flow');
  
  const [currentPhase, setCurrentPhase] = useState(() => {
    // Initialize state based on dev mode if active
    if (isDevMode && devFlowParam) {
      console.log('INIT: Setting initial phase from dev mode:', devFlowParam);
      return devFlowParam;
    }
    return 'payment_pending';
  });
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('Payment Setup');
  const [isComplete, setIsComplete] = useState(false);
  const [useEmbeddedPayment, setUseEmbeddedPayment] = useState(!isFreeMode);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [clinicData, setClinicData] = useState(null);
  const [activationError, setActivationError] = useState(null);
  const [isActivatingFreeMode, setIsActivatingFreeMode] = useState(false);

  useEffect(() => {
    if (isFreeMode) {
      setUseEmbeddedPayment(false);
    }
  }, [isFreeMode]);

  const formatPlanName = (value) => {
    if (!value) return 'Selected Plan';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const freeModeMessage = billingConfig.freeModeMessage || 'Activate your MedInsight workspace without entering billing details.';
  const freeModeTrialDays = billingConfig.freeModeTrialDays || 60;
  const freeModePlanId = billingConfig.freeModePlanId || 'free';
  const displayedPlanName = formatPlanName(isFreeMode ? freeModePlanId : planData?.plan_id);
  const complimentaryAccessLabel = freeModeTrialDays
    ? `${freeModeTrialDays}-day complimentary access`
    : 'Complimentary access period';
  const completedFeatures = isFreeMode
    ? [
        'Workspace provisioned',
        'Database created',
        'Pabau integration ready',
        'Automation system configured',
        complimentaryAccessLabel
      ]
    : [
        'Payment method secured',
        'Database provisioned',
        'Pabau API connected',
        'Automation system configured',
        '14-day trial activated'
      ];

  // Phase progression: payment_pending -> processing -> completed
  const phases = [
    { id: 'payment_pending', label: isFreeMode ? 'Activate Plan' : 'Payment Setup', progress: 0 },
    { id: 'processing', label: 'Account Creation', progress: 50 },
    { id: 'completed', label: 'Setup Complete', progress: 100 }
  ];

  // Handle URL parameter flow override (for dev testing)
  useEffect(() => {
    if (!isDevMode || !devFlowParam) {
      return;
    }
    
    console.log('DEV MODE: Setting phase to:', devFlowParam);
    
    switch (devFlowParam) {
      case 'payment_pending':
        setCurrentPhase('payment_pending');
        setCurrentStep('Payment Setup');
        setProgress(0);
        break;
      case 'processing':
        setCurrentPhase('processing');
        setCurrentStep('Setting up your account...');
        setProgress(50);
        break;
      case 'completed':
        setCurrentPhase('completed');
        setCurrentStep('Setup Complete');
        setProgress(100);
        setIsComplete(true);
        break;
    }
  }, [isDevMode, devFlowParam, searchParams]);

  // Handle different flow states (but NEVER override dev mode)
  useEffect(() => {
    if (isDevMode) {
      return;
    }
    
    switch (currentFlow) {
      case 'payment_pending':
        setCurrentPhase('payment_pending');
        setProgress(0);
        setCurrentStep('Payment Setup');
        break;
      case 'processing':
        setCurrentPhase('processing');
        setProgress(50);
        setCurrentStep('Setting up your account...');
        checkOnboardingProgress();
        break;
      case 'completed':
        setCurrentPhase('completed');
        setProgress(100);
        setCurrentStep('Setup Complete');
        setIsComplete(true);
        break;
    }
  }, [currentFlow, isDevMode]);

  const checkOnboardingProgress = () => {
    let attempts = 0;
    const maxAttempts = 60; // 2 minutes max
    
    const checkInterval = setInterval(async () => {
      attempts++;
      
      try {
        // Check lead status
        const response = await fetch(`${getApiBaseUrl()}/onboarding/lead-success/${leadId}`);
        const result = await response.json();
        
        if (result.success && result.status === 'completed') {
          // Account is ready, setup complete
          clearInterval(checkInterval);
          setCurrentPhase('completed');
          setProgress(100);
          setCurrentStep('Setup Complete');
          setClinicData(result.clinic);
          setIsComplete(true);
          
          // Store auth token
          if (result.access_token) {
            localStorage.setItem('auth_token', result.access_token);
            localStorage.setItem('user', JSON.stringify(result.user || {}));
          }
        } else if (result.status === 'processing') {
          // Still processing - update progress
          const estimatedProgress = Math.min(50 + (attempts / maxAttempts) * 50, 100);
          setProgress(estimatedProgress);
          setCurrentStep(result.message || 'Setting up your account...');
        } else if (attempts >= maxAttempts) {
          // Timeout
          clearInterval(checkInterval);
          setCurrentPhase('error');
          setCurrentStep('Setup timed out - please contact support');
        }
      } catch (error) {
        console.error('Error checking progress:', error);
        if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          setCurrentPhase('error');
          setCurrentStep('Unable to check setup progress');
        }
      }
    }, 2000);
  };

  // Handle manual checkout creation (for standalone Stripe)
  const handleStartTrial = async () => {
    if (onCreateCheckout && !isLoading) {
      console.log('Starting checkout creation...');
      await onCreateCheckout();
    }
  };

  const handleFreeModeActivation = async () => {
    if (!onActivateFreeMode || isActivatingFreeMode) {
      return;
    }

    setActivationError(null);
    setIsActivatingFreeMode(true);

    try {
      const result = await onActivateFreeMode();

      if (result?.success) {
        setCurrentPhase('processing');
        setProgress(50);
        setCurrentStep('Activating your account...');
        checkOnboardingProgress();
      } else {
        setActivationError(result?.error || 'Failed to activate complimentary access. Please try again.');
      }
    } catch (activationErrorCaught) {
      console.error('Free mode activation error:', activationErrorCaught);
      setActivationError('Unable to activate your complimentary access. Please retry.');
    } finally {
      setIsActivatingFreeMode(false);
    }
  };

  // Handle go to dashboard
  const handleGoToDashboard = () => {
    navigate('/dashboard?welcome=true&trial_started=true');
  };

  // Get the correct API base URL
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    } else {
      return process.env.REACT_APP_API_URL || '/api';
    }
  };

  // Render payment pending phase
  const renderPaymentPending = () => (
    <div className="setup-content">
      <div className="setup-icon">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z"/>
          <path d="M8 1v4"/>
          <path d="M16 1v4"/>
        </svg>
      </div>
      
      <div className="setup-header">
        <h2>{isFreeMode ? 'Activate Your Free Plan' : 'Start Your 14-Day Free Trial'}</h2>
        <p>
          {isFreeMode
            ? freeModeMessage
            : "Enter your payment details to begin your free trial. You won't be charged until the trial ends."}
        </p>
        {isFreeMode && (
          <p className="setup-subtext">
            Enjoy {complimentaryAccessLabel} on the {displayedPlanName} plan. No card required and you can upgrade anytime.
          </p>
        )}
      </div>

      {isFreeMode ? (
        <>
          <div className="trial-benefits">
            <h3>You're getting:</h3>
            <ul>
              <li>✅ Full MedInsight access with real-time analytics</li>
              <li>✅ Tenant provisioning and Pabau integration setup</li>
              <li>✅ Automation workflows ready to configure</li>
              <li>✅ Launch support from the MedInsight team</li>
              <li>✅ Cancel or upgrade whenever you’re ready</li>
            </ul>
          </div>

          {activationError && (
            <div style={{ marginTop: '16px', color: '#dc2626', fontWeight: 500 }}>
              {activationError}
            </div>
          )}
          
          <div className="trial-actions">
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={handleFreeModeActivation}
              disabled={isActivatingFreeMode}
            >
              {isActivatingFreeMode ? 'Activating...' : 'Activate Free Plan'}
            </button>
          </div>
        </>
      ) : (
        <>
          {useEmbeddedPayment ? (
            <div className="embedded-payment-container">
              <EmbeddedPayment 
                leadId={leadId}
                onPaymentSuccess={() => {
                  setCurrentPhase('processing');
                  setProgress(50);
                  setCurrentStep('Setting up your account...');
                  checkOnboardingProgress();
                }}
                onPaymentError={(error) => {
                  console.error('Payment error:', error);
                  setCurrentPhase('payment_pending');
                  setProgress(0);
                  setCurrentStep('Payment Setup');
                }}
                setIsLoading={setLocalIsLoading}
                paymentPreload={paymentPreload}
              />
              
              <div className="payment-options">
                <p>
                  Prefer to enter payment details separately?{' '}
                  <button 
                    type="button" 
                    className="link-button"
                    onClick={() => setUseEmbeddedPayment(false)}
                  >
                    Use external checkout instead
                  </button>
                </p>
              </div>
            </div>
          ) : (
            <div className="external-payment-container">
              <div className="trial-benefits">
                <h3>What's included in your free trial:</h3>
                <ul>
                  <li>✅ Full access to all features</li>
                  <li>✅ Import your Pabau data</li>
                  <li>✅ Set up automation campaigns</li>
                  <li>✅ No setup fees</li>
                  <li>✅ Cancel anytime</li>
                </ul>
              </div>
              
              <div className="trial-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={handleStartTrial}
                  disabled={isLoading}
                >
                  {isLoading ? 'Starting...' : 'Start 14-Day Free Trial'}
                </button>
              </div>
              
              <div className="payment-options">
                <p>
                  Want to enter payment details here?{' '}
                  <button 
                    type="button" 
                    className="link-button"
                    onClick={() => setUseEmbeddedPayment(true)}
                  >
                    Use embedded form instead
                  </button>
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Render processing phase
  const renderProcessing = () => (
    <div className="setup-content">
      <div className="setup-icon">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      </div>
      
      <div className="setup-header">
        <h2>{isFreeMode ? 'Activating Your Workspace' : 'Setting Up Your Account'}</h2>
        <p>
          {isFreeMode
            ? 'Hang tight while we prepare your MedInsight workspace...'
            : 'Please wait while we prepare your trial account...'}
        </p>
      </div>

      <div className="setup-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="progress-text">
          {currentStep}
        </div>
      </div>

      <div className="setup-steps">
        <div className="step-item active">
          <div className="step-icon">✅</div>
          <div className="step-text">
            {isFreeMode ? 'Free plan activated' : 'Payment verified'}
          </div>
        </div>
        <div className="step-item active">
          <div className="step-icon">⚙️</div>
          <div className="step-text">Creating your database</div>
        </div>
        <div className="step-item active">
          <div className="step-icon">🔗</div>
          <div className="step-text">Configuring Pabau integration</div>
        </div>
        <div className="step-item active">
          <div className="step-icon">🎯</div>
          <div className="step-text">Setting up automation system</div>
        </div>
      </div>

      <div className="setup-note">
        <p>
          <strong>💡 Note:</strong> {isFreeMode
            ? 'Provisioning usually takes 1-2 minutes. We will move you to the dashboard as soon as everything is ready.'
            : 'This process typically takes 1-2 minutes. Your trial account will be ready soon!'}
        </p>
      </div>
    </div>
  );

  // Render completed phase
  const renderCompleted = () => (
    <div className="setup-content">
      <div className="setup-icon success">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22,4 12,14.01 9,11.01"/>
        </svg>
      </div>
      
      <div className="setup-header">
        <h2>{isFreeMode ? '🎉 Your MedInsight workspace is live!' : '🎉 Welcome to MedInsight Pro!'}</h2>
        <p>
          {isFreeMode
            ? 'Your complimentary access is active. Explore MedInsight and upgrade whenever you are ready.'
            : 'Your account is ready! Your 14-day free trial has started.'}
        </p>
      </div>

      <div className="setup-summary">
        <div className="summary-card">
          <h3>📊 {isFreeMode ? 'Access Details' : 'Trial Details'}</h3>
          <div className="summary-item">
            <strong>Plan:</strong> {displayedPlanName}
          </div>
          <div className="summary-item">
            <strong>{isFreeMode ? 'Complimentary period' : 'Trial'}:</strong> {isFreeMode ? complimentaryAccessLabel : '14 days remaining'}
          </div>
          {clinicData && (
            <div className="summary-item">
              <strong>Clinic ID:</strong> {clinicData.id}
            </div>
          )}
        </div>

        <div className="summary-card">
          <h3>✅ What's Ready</h3>
          <div className="feature-list">
            {completedFeatures.map((item) => (
              <div className="feature-item" key={item}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="setup-actions">
        <button
          type="button"
          className="btn btn-primary btn-large"
          onClick={handleGoToDashboard}
        >
          Go to Dashboard →
        </button>
      </div>
    </div>
  );

  // Debug logging for render
  console.log('AccountSetupFlow RENDER:', {
    currentPhase,
    progress,
    currentStep,
    clinicData,
    leadId
  });

  return (
    <div className="step-content">
      <div className="complete-setup-content">
        {currentPhase === 'payment_pending' && renderPaymentPending()}
        {currentPhase === 'processing' && renderProcessing()}
        {currentPhase === 'completed' && renderCompleted()}
        {currentPhase === 'error' && <div>Error occurred</div>}
      </div>
    </div>
  );
};

export default AccountSetupFlow; 
